import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getOutgoingTransportList,
  addOutgoingTransportToBusiness,
  updateOutgoingTransport,
  deleteOutgoingTransport,
} from "../../../../../../apis/Business/OutgoingTransportApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getTransportCountryData,
  getTransportDeliveriesData,
} from "../../../../../../apis/Dropdown";

const OutgoingTransportTable = ({
  businessId,
  subcategory,
  subcategory_description,
  subcategoryId,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownVehicle, setDropdownVehicle] = useState([]);
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getTransportDeliveriesData({
      setDropdownData: setDropdownVehicle,
    });
    getTransportCountryData({
      setDropdownData: setDropdownCountries,
    });
    getOutgoingTransportList({
      businessId: businessId,
      setObjects: setData,
      subcategory: subcategory,
      subcategory_description: subcategory_description,
      subcategoryId: subcategoryId,
    });
  }, [businessId, subcategory, subcategoryId, subcategory_description]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownVehicle?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["means_transportation.value"]?.value ||
          row.means_transportation?.value;

        if (materialValue) {
          const selectedMaterial = dropdownVehicle.find(
            (option) =>
              option?.dropdown_name === materialValue ||
              option?.dropdown_name_en === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownVehicle]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "means_transportation.value": {
        targetField: "total_weight_delivery",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) =>
              option?.dropdown_name === selectedValue ||
              option?.dropdown_name_en === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "means_transportation.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateOutgoingTransport({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        means_transportation: editedItem["means_transportation.value"]
          ? editedItem["means_transportation.value"].value
          : editedItem["means_transportation"]
          ? editedItem["means_transportation"].value
          : null,
        country_arrival: editedItem["country_arrival.value"]
          ? editedItem["country_arrival.value"].value
          : editedItem["country_arrival"]
          ? editedItem["country_arrival"].value
          : null,
        subcategoryId: subcategoryId,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [
      businessId,
      data,
      setData,
      subcategory,
      subcategory_description,
      subcategoryId,
    ]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addOutgoingTransportToBusiness({
        businessId,
        ...newItem,
        means_transportation: newItem["means_transportation.value"]
          ? newItem["means_transportation.value"].value
          : null,
        country_arrival: newItem["country_arrival.value"]
          ? newItem["country_arrival.value"].value
          : null,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        subcategoryId: subcategoryId,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory, subcategory_description, subcategoryId]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteOutgoingTransport({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "client",
        header: t("CLIENT"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "80px",
      },
      {
        accessor: "means_transportation.value",
        header: t("MEANS"),
        type: "dropdown",
        dropdownData: dropdownVehicle,
        disableSortBy: true,
        width: "110px",
      },
      {
        accessor: "transport_distance",
        header: `${t("TRANSPORT_DISTANCE")} *`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("TIP_DISTANCE_TRANSPORT"),
        unit: "km",
      },
      {
        accessor: "manuel",
        header: t("TRANSPORT_MANUEL"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: "kgCO₂e/tonnes-km",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUEL_TRANSPORT"),
        optional: true,
      },
      {
        accessor: "country_arrival.value",
        header: `${t("TRANSPORT_COUNTRY_ARRIVAL")}*`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "total_weight_delivery",
        header: (props) => {
          const rowId = props.row?.original?.id;
          return `${t("TOTAL_WEIGHT_DELIVERY")} (${
            unitMaterials[rowId] || "xx"
          })`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        unit: (rowId) => `${unitMaterials[rowId] || "xx"}`,
      },
      {
        accessor: "manuel_distribution",
        header: t("STORAGE_DISTRIBUTOR"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        unit: "tCO₂e",
        tipHeader: "Tip",
        tipBody: t("TIP_STORAGE_TRANSPORT"),
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownCountries, dropdownVehicle, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    manuel_distribution: { label: t("TOTAL_STORAGE_DISTRIBUTOR") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "means_transportation.value": dropdownVehicle,
      "country_arrival.value": dropdownCountries,
    }),
    [dropdownVehicle, dropdownCountries]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default OutgoingTransportTable;
